import PropTypes from 'prop-types'
import React, { forwardRef } from 'react'

import { TextInput } from '@/components/forms/TextInput'
import { SegmentedTabs } from '@/components/ui/SegmentedTabs'

export const ENTRY_METHOD = {
  PASTE: 'paste',
  MANUAL: 'manual',
}

const TABS = [
  { id: ENTRY_METHOD.PASTE, label: 'Paste a list' },
  { id: ENTRY_METHOD.MANUAL, label: 'Enter manually' },
]

const PANEL_ID = 'log-ros-entry-panel'

/**
 * The left half of the Log ROs screen: pick a way in, produce RO numbers.
 *
 * Presentational — the page owns every piece of state so that both methods feed one
 * preview. (Reading a Service Daily Log upload is WARP-947; it gets a tab here once built.)
 *
 * `ref` is the manual input, which the page refocuses after each Enter so a worker can
 * key a run of numbers without reaching for the mouse.
 */
const EntryPanel = forwardRef(
  (
    {
      activeMethod,
      chips,
      manualError,
      manualValue,
      onManualAdd,
      onManualValueChange,
      onMethodChange,
      onPastePreview,
      onPasteTextChange,
      onRemoveChip,
      pasteText,
      working,
    },
    manualInputRef,
  ) => (
    <div className="rounded-2xl bg-white p-6 shadow-sm">
      <SegmentedTabs
        activeTab={activeMethod}
        ariaLabel="How to add repair orders"
        onChange={onMethodChange}
        panelId={PANEL_ID}
        tabs={TABS}
      />

      <div className="mt-5" id={PANEL_ID} role="tabpanel">
        {activeMethod === ENTRY_METHOD.PASTE && (
          <div className="space-y-3">
            <label
              className="text-charcoal-700 block text-[10px] font-bold uppercase tracking-widest"
              htmlFor="log-ros-paste"
            >
              Paste RO numbers
            </label>
            <textarea
              className="border-charcoal-200 bg-background w-full rounded-lg border p-3 font-mono text-sm"
              id="log-ros-paste"
              onChange={(event) => onPasteTextChange(event.target.value)}
              placeholder={'695607\n695662\n695530'}
              rows={12}
              value={pasteText}
            />
            <p className="text-charcoal-500 text-xs">
              One per line, or separated by commas, spaces, or tabs. Everything you paste shows in
              the preview before anything is logged.
            </p>
            <div className="flex justify-end">
              <button
                className="rounded border border-blue-800 px-4 py-2 text-xs font-bold uppercase tracking-widest text-blue-800 transition-colors hover:bg-blue-800/5 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={pasteText.trim() === '' || working}
                onClick={onPastePreview}
                type="button"
              >
                Preview ROs
              </button>
            </div>
          </div>
        )}

        {activeMethod === ENTRY_METHOD.MANUAL && (
          <div className="space-y-3">
            <form
              className="flex items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault()
                onManualAdd()
              }}
            >
              <TextInput
                aria-label="Repair order number"
                containerClassName="flex-1"
                fullWidth
                id="log-ros-manual"
                label="Repair Order Number"
                onChange={(event) => onManualValueChange(event.target.value)}
                placeholder="695607"
                ref={manualInputRef}
                value={manualValue}
              />
              <button
                className="h-[46px] shrink-0 rounded border border-blue-800 px-4 text-xs font-bold uppercase tracking-widest text-blue-800 transition-colors hover:bg-blue-800/5 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={manualValue.trim() === ''}
                type="submit"
              >
                Add
              </button>
            </form>

            {manualError ? (
              <p className="text-red text-xs">{manualError}</p>
            ) : (
              <p className="text-charcoal-500 text-xs">
                Press Enter to add it and start the next one. Each number is checked against what
                this dealer already has.
              </p>
            )}

            {chips.length > 0 && (
              <div className="bg-background flex flex-wrap gap-2 rounded-xl p-3">
                {chips.map((chip) => (
                  <span
                    className="border-charcoal-200 inline-flex items-center gap-2 rounded-full border bg-white py-1 pl-3 pr-1 font-mono text-xs"
                    key={chip.id}
                  >
                    {chip.roNumber}
                    <button
                      aria-label={`Remove ${chip.roNumber}`}
                      className="text-charcoal-400 hover:bg-charcoal-100 hover:text-charcoal-900 flex h-5 w-5 items-center justify-center rounded-full"
                      onClick={() => onRemoveChip(chip.id)}
                      type="button"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  ),
)

EntryPanel.displayName = 'EntryPanel'

EntryPanel.propTypes = {
  activeMethod: PropTypes.oneOf(Object.values(ENTRY_METHOD)).isRequired,
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      roNumber: PropTypes.string.isRequired,
    }),
  ).isRequired,
  manualError: PropTypes.string,
  manualValue: PropTypes.string.isRequired,
  onManualAdd: PropTypes.func.isRequired,
  onManualValueChange: PropTypes.func.isRequired,
  onMethodChange: PropTypes.func.isRequired,
  onPastePreview: PropTypes.func.isRequired,
  onPasteTextChange: PropTypes.func.isRequired,
  onRemoveChip: PropTypes.func.isRequired,
  pasteText: PropTypes.string.isRequired,
  working: PropTypes.bool,
}

export default EntryPanel
